import { trackLoggedOutRetailersFilterPillRowView } from "@faire/web-api/events/loggedOutRetailers/view/filterPillRow";
import { trackRetailersFilterInteraction } from "@faire/web-api/events/retailers/actionUnknown/filterInteraction";
import { trackRetailersFilterClickedClick } from "@faire/web-api/events/retailers/click/filterClicked";
import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { Row } from "@faire/web/slate/Layout";
import { LoadingSkeleton } from "@faire/web/slate/LoadingSkeleton";
import { getSpacing } from "@faire/web/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { FilterToggle } from "@faire/retailer/components/Filters/__internal__/FilterToggle";
import {
  getMultipleOptionalSelectionsFilters,
  getSortedFilterSections,
  getToggleFilters,
  selectIfSelectedAllSubOptions,
  syncToggleFilters,
  updateAllFilterKeys,
} from "@faire/retailer/components/Filters/FilterUtils";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { useTopOfPageContentStore } from "@faire/retailer/stores/ui/TopOfPageContentStore";

interface IProps {
  name: string;
  className?: string;
  wrap?: boolean;
}

export const ToggleFilterRow: React.FC<IProps> = ({
  name,
  className,
  wrap = false,
}) => {
  const {
    data: { filterSections, isNewFilterSelected },
    setFilterState,
  } = useFilterStore(["filterSections", "isNewFilterSelected"]);
  const { isDesktopAndAbove } = useViewport();
  const { headerHeight } = useTopOfPageContentStore(["headerHeight"]);
  const sortedFilterSections = getSortedFilterSections(filterSections, false);
  const toggleFilters = getToggleFilters(sortedFilterSections);
  const multipleOptionalSelectionsFilters =
    getMultipleOptionalSelectionsFilters(filterSections);
  const location = useLocation();

  const viewRef = useDoActionWhenVisible(() => {
    const joinedFilterKeys = toggleFilters
      .concat(multipleOptionalSelectionsFilters)
      .flatMap((section) => {
        return section.options.map((option) => option.key);
      })
      .join(";");
    trackLoggedOutRetailersFilterPillRowView(
      joinedFilterKeys,
      location.pathname + location.search
    );
  });

  const scrollToResults = useCallback(() => {
    const window = getWindowOrThrow();
    const rowHeight = isDesktopAndAbove ? 64 : 56;
    const element = getDocumentOrThrow().getElementById(
      "search-page-results-wrapper"
    );
    const elementPos = element?.getBoundingClientRect().top ?? 0;
    const offsetPos = elementPos + window.scrollY - headerHeight - rowHeight;
    window.scrollTo?.({
      top: offsetPos,
      behavior: "smooth",
    });
  }, [headerHeight, isDesktopAndAbove]);

  const handleFilterSelected = (
    section: IFilterSection,
    filterKey: string,
    value: boolean
  ) => {
    trackRetailersFilterClickedClick(name, filterKey);
    trackRetailersFilterInteraction(
      name,
      wrap ? "toggle_filter_click_modal" : "toggle_filter_click_bar",
      filterKey
    );
    const sectionsUpdated = updateAllFilterKeys(
      filterSections,
      filterKey,
      value
    );
    syncToggleFilters(section, sectionsUpdated);

    if (section.field_name === IFilterSection.FieldName.TOP_CATEGORY_PILLS) {
      filterSections
        .find((s) => s.field_name === IFilterSection.FieldName.CATEGORY)
        ?.options.forEach(selectIfSelectedAllSubOptions);
    }

    setFilterState((currentState) => ({
      ...currentState,
      filterSections: [...sectionsUpdated],
      isNewFilterSelected: true,
    }));

    if (!isLoggedInRetailerOrBrandPreview()) {
      scrollToResults();
    }
  };

  if (
    sortedFilterSections.length > 0 &&
    toggleFilters.length === 0 &&
    multipleOptionalSelectionsFilters.length === 0
  ) {
    return null;
  }

  if (sortedFilterSections.length === 0) {
    return (
      <Row>
        {Array.from({ length: 2 }, (_value, index) => (
          <SpacedLoadingSkeleton
            key={`toggleFilterPlaceholder_${index}`}
            width="140px"
            height="40px"
            borderRadius="40px"
          />
        ))}
      </Row>
    );
  }

  return (
    <Wrapper className={className} $isWrap={wrap} ref={viewRef}>
      {toggleFilters.map((filterSection) => (
        <FilterToggle
          key={`${filterSection.display_name}`}
          section={filterSection}
          onChange={handleFilterSelected}
          disabled={isNewFilterSelected}
        />
      ))}
      {multipleOptionalSelectionsFilters.map((filterSection) =>
        filterSection.options.map((option) => (
          <FilterToggle
            key={`${option.key}`}
            section={IFilterSection.build({
              ...filterSection,
              display_name: option.display_name,
              options: [option],
            })}
            onChange={handleFilterSelected}
            disabled={isNewFilterSelected}
          />
        ))
      )}
    </Wrapper>
  );
};

// eslint-disable-next-line @faire/prefer-slate-attrs -- false alarm (display: none refers to scrollbar)
const Wrapper = styled(Row).attrs({
  gap: "1x",
})<{ $isWrap: boolean }>`
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${(props) =>
    props.$isWrap
      ? css`
          flex-wrap: wrap;
        `
      : css`
          overflow-x: auto;
        `}
`;

const SpacedLoadingSkeleton = styled(LoadingSkeleton)`
  margin-right: ${getSpacing("1x")};
`;
